/**
 * MFA Settings Page
 * Allows users to configure their multi-factor authentication settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Grid,
} from '@mui/material';
import {
  PhoneAndroid,
  Email,
  VpnKey,
  ContentCopy,
  Download,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { authService } from '../../services/authService';
import { MfaStatus, TotpSetupResponse } from '../../types/auth';
import { useAcao } from '../../authorization/useVeredito';
import { PageShell } from '../../components/common';
import MfaEnforcementSection from './MfaEnforcementSection';

const MfaSettingsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const exigirMfa = useAcao('mfa.enforce', 'Exigir MFA por função');

  const [status, setStatus] = useState<MfaStatus | null>(null);
  const [loading, setLoading] = useState(true);

  // TOTP setup dialog
  const [totpDialog, setTotpDialog] = useState(false);
  const [totpSetup, setTotpSetup] = useState<TotpSetupResponse | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);

  // Recovery codes dialog
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [recoveryDialog, setRecoveryDialog] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      setLoading(true);
      const mfaStatus = await authService.getMfaStatus();
      setStatus(mfaStatus);
    } catch {
      enqueueSnackbar('Erro ao carregar status MFA', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSetupTotp = async () => {
    try {
      setTotpLoading(true);
      const setup = await authService.initiateTotpSetup();
      setTotpSetup(setup);
      setTotpDialog(true);
    } catch {
      enqueueSnackbar('Erro ao iniciar configuração TOTP', { variant: 'error' });
    } finally {
      setTotpLoading(false);
    }
  };

  const handleConfirmTotp = async () => {
    if (!totpCode || totpCode.length < 6) return;
    try {
      setTotpLoading(true);
      const result = await authService.confirmTotpSetup(totpCode);
      setRecoveryCodes(result.recoveryCodes);
      setTotpDialog(false);
      setTotpCode('');
      setRecoveryDialog(true);
      await loadStatus();
      enqueueSnackbar('TOTP configurado com sucesso!', { variant: 'success' });
    } catch {
      enqueueSnackbar('Código inválido. Tente novamente.', { variant: 'error' });
    } finally {
      setTotpLoading(false);
    }
  };

  const handleEnableEmail = async () => {
    try {
      const result = await authService.enableEmailMfa();
      setRecoveryCodes(result.recoveryCodes);
      setRecoveryDialog(true);
      await loadStatus();
      enqueueSnackbar('MFA por e-mail habilitado!', { variant: 'success' });
    } catch {
      enqueueSnackbar('Erro ao habilitar MFA por e-mail', { variant: 'error' });
    }
  };

  const handleDisable = async () => {
    try {
      await authService.disableMfa();
      await loadStatus();
      enqueueSnackbar('MFA desabilitado', { variant: 'info' });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message :'Erro ao desabilitar MFA', { variant: 'error' });
    }
  };

  const handleSetMethod = async (method: 'totp' | 'email') => {
    try {
      await authService.setMfaMethod(method);
      await loadStatus();
      enqueueSnackbar(`Método alterado para ${method === 'totp' ? 'App autenticador' : 'E-mail'}`, { variant: 'success' });
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message :'Erro ao alterar método', { variant: 'error' });
    }
  };

  const handleRegenerateCodes = async () => {
    try {
      const result = await authService.regenerateRecoveryCodes();
      setRecoveryCodes(result.recoveryCodes);
      setRecoveryDialog(true);
      await loadStatus();
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message :'Erro ao regenerar códigos', { variant: 'error' });
    }
  };

  const handleCopyCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard.writeText(recoveryCodes.join('\n'));
    enqueueSnackbar('Códigos copiados!', { variant: 'success' });
  };

  const handleDownloadCodes = () => {
    if (!recoveryCodes) return;
    const content = `MEV-Med - Códigos de Recuperação MFA\nGerados em: ${new Date().toLocaleString('pt-BR')}\n\n${recoveryCodes.join('\n')}\n\nGuarde estes códigos em local seguro. Cada código pode ser usado apenas uma vez.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mev-med-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const breadcrumbs = [
    { label: 'Início', path: '/' },
    { label: 'Perfil', path: '/profile' },
    { label: 'Segurança MFA' },
  ];

  if (loading) {
    return (
      <PageShell breadcrumbs={breadcrumbs} title="Autenticação em Duas Etapas (MFA)">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell
      breadcrumbs={breadcrumbs}
      title="Autenticação em Duas Etapas (MFA)"
    >
        {/* Status Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h6">Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <Chip
                    icon={status?.isEnabled ? <CheckCircle /> : undefined}
                    label={status?.isEnabled ? 'Habilitado' : 'Desabilitado'}
                    color={status?.isEnabled ? 'success' : 'default'}
                  />
                  {status?.isEnabled && (
                    <Chip
                      label={status.method === 'totp' ? 'App Autenticador' : 'E-mail'}
                      variant="outlined"
                      size="small"
                    />
                  )}
                  {status?.enforcedByAdmin && (
                    <Chip
                      icon={<Warning />}
                      label="Exigido pelo administrador"
                      color="warning"
                      size="small"
                    />
                  )}
                </Box>
              </Box>
              {status?.isEnabled && !status.enforcedByAdmin && (
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={handleDisable}
                >
                  Desabilitar MFA
                </Button>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Setup Options */}
        {!status?.isEnabled && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Configurar MFA
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <PhoneAndroid sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        App Autenticador
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Google Authenticator, Authy ou similar
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleSetupTotp}
                        disabled={totpLoading}
                        startIcon={totpLoading ? <CircularProgress size={16} /> : undefined}
                      >
                        Configurar
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Email sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        E-mail
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Código enviado por e-mail a cada login
                      </Typography>
                      <Button variant="contained" onClick={handleEnableEmail}>
                        Habilitar
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Method Switch (when enabled) */}
        {status?.isEnabled && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Método Preferido</Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant={status.method === 'totp' ? 'contained' : 'outlined'}
                  startIcon={<PhoneAndroid />}
                  onClick={() => handleSetMethod('totp')}
                >
                  App Autenticador
                </Button>
                <Button
                  variant={status.method === 'email' ? 'contained' : 'outlined'}
                  startIcon={<Email />}
                  onClick={() => handleSetMethod('email')}
                >
                  E-mail
                </Button>
              </Box>
              {!status?.isEnabled || status.method !== 'totp' ? null : null}
            </CardContent>
          </Card>
        )}

        {/* Recovery Codes Status */}
        {status?.isEnabled && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VpnKey />
                    Códigos de Recuperação
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {status.hasRecoveryCodes
                      ? `${status.recoveryCodesRemaining} códigos restantes`
                      : 'Nenhum código gerado'}
                  </Typography>
                </Box>
                <Button variant="outlined" onClick={handleRegenerateCodes}>
                  {status.hasRecoveryCodes ? 'Regenerar' : 'Gerar Códigos'}
                </Button>
              </Box>
              {status.hasRecoveryCodes && status.recoveryCodesRemaining <= 2 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Poucos códigos restantes. Considere regenerar seus códigos de recuperação.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/*
          O único bloco desta tela que não é da própria conta: ele lista as funções do sistema,
          e a lista vem de um endpoint que recusa quem não administra. Desabilitá-lo com o
          motivo, como manda a política, exigiria carregar a lista antes de saber que não pode —
          o que devolve erro em vez de explicação. Por isso aqui ele continua saindo de cena.
        */}
        {!exigirMfa.disabled && (
          <Box sx={{ mt: 3 }}>
            <MfaEnforcementSection />
          </Box>
        )}

      {/* TOTP Setup Dialog */}
      <Dialog open={totpDialog} onClose={() => setTotpDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Configurar App Autenticador</DialogTitle>
        <DialogContent>
          {totpSetup && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Escaneie o QR code com seu aplicativo autenticador (Google Authenticator, Authy, etc.)
              </Typography>
              <Box
                component="img"
                src={totpSetup.qrCodeDataUrl}
                alt="QR Code"
                sx={{ width: 200, height: 200, mx: 'auto', mb: 2 }}
              />
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 2 }}>
                Ou digite manualmente: <code style={{ userSelect: 'all' }}>{totpSetup.secret}</code>
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" sx={{ mb: 1 }}>
                Digite o código de 6 dígitos gerado pelo app:
              </Typography>
              <TextField
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '8px', fontFamily: 'monospace' },
                  inputMode: 'numeric',
                }}
                sx={{ width: 200 }}
                autoFocus
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setTotpDialog(false); setTotpCode(''); }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmTotp}
            disabled={totpCode.length < 6 || totpLoading}
          >
            {totpLoading ? <CircularProgress size={20} /> : 'Confirmar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recovery Codes Dialog */}
      <Dialog open={recoveryDialog} onClose={() => setRecoveryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Códigos de Recuperação</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Guarde estes códigos em um local seguro. Cada código pode ser usado apenas uma vez para
            acessar sua conta caso você perca acesso ao seu método MFA principal.
          </Alert>
          <Box
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 2,
              p: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1,
              fontFamily: 'monospace',
              fontSize: '1.1rem',
              textAlign: 'center',
            }}
          >
            {recoveryCodes?.map((code, i) => (
              <Typography key={i} sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                {code}
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
            <Tooltip title="Copiar códigos">
              <IconButton onClick={handleCopyCodes}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
            <Tooltip title="Baixar como arquivo">
              <IconButton onClick={handleDownloadCodes}>
                <Download />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setRecoveryDialog(false)}>
            Entendi, salvei os códigos
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default MfaSettingsPage;
